// Mobile app wizard shell: loads the record, renders the step rail with
// completeness marks + progress (reference "X/10 steps complete"), switches
// steps in place (no reloads) and passes the save pipe down.
import { useCallback, useEffect, useRef, useState } from "react";
import type { Locale, PartnerDto } from "../../../shared/dto";
import type { AppWizardResponse, AppWizardStep } from "../../../shared/app-wizard";
import {
  consumeWizardFocus,
  fetchWizard,
  putWizardStep,
  type StepProps,
  type WizardLoadFailure,
  type WizardT
} from "./wizard-shared";
import { StepBasics, StepOverview } from "./steps-basics";
import { StepBranding } from "./steps-branding";
import { StepAppleCopy, StepAppleDeveloper } from "./steps-apple";
import { StepGoogleCopy, StepScreenshots } from "./steps-google-graphics";
import { StepLegal, StepSubscriptions } from "./steps-commerce";
import { StepReview } from "./steps-review";
import { actionIssueFrom, actionIssueFromResponse, actionIssueFromThrown, type ActionIssue } from "../../action-error";

const STEP_COMPONENTS: Record<AppWizardStep, (props: StepProps) => React.JSX.Element> = {
  overview: StepOverview,
  basics: StepBasics,
  branding: StepBranding,
  apple_developer: StepAppleDeveloper,
  apple_copy: StepAppleCopy,
  google_copy: StepGoogleCopy,
  screenshots: StepScreenshots,
  subscriptions: StepSubscriptions,
  legal: StepLegal,
  review: StepReview
};

export function AppWizard(props: {
  locale: Locale;
  csrfToken: string;
  membershipRole: "owner" | "collaborator";
  appleConfirmed: boolean;
  appleChecklist?: React.ReactNode;
  partner?: Pick<PartnerDto, "displayName" | "calendlyUrl"> | null;
  t: WizardT;
  /** First load: reports submittedAt (null = not submitted) to the host section. */
  onLoaded?: (submittedAt: string | null) => void;
  /** Submit went through: AppTrack refreshes home (orange dot off, banner off). */
  onSubmitted: () => Promise<void> | void;
}) {
  const [data, setData] = useState<AppWizardResponse | null>(null);
  const [failure, setFailure] = useState<WizardLoadFailure | null>(null);
  const [active, setActive] = useState<AppWizardStep>("overview");
  const [submitting, setSubmitting] = useState(false);
  const loadSequence = useRef(0);
  const onLoadedRef = useRef(props.onLoaded);
  onLoadedRef.current = props.onLoaded;

  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setFailure(null);
    const result = await fetchWizard();
    if (sequence !== loadSequence.current) return;
    if (!result.ok) {
      setFailure(result.failure);
      return;
    }
    const response = result.data;
    setData(response);
    onLoadedRef.current?.(response.wizard.submitted ? (response.wizard.submittedAt ?? new Date().toISOString()) : null);
    const focus = consumeWizardFocus();
    const steps = response.states.map((state) => state.step);
    if (focus && (steps as string[]).includes(focus)) {
      setActive(focus as AppWizardStep);
    } else if (response.wizard.submitted) {
      setActive("review");
    } else {
      // Land on the first unfinished step; a fresh wizard starts at the
      // welcome screen (overview counts as done immediately).
      const next = response.states.find((state) => state.step !== "overview" && state.status !== "done");
      setActive(response.wizard.savedSteps.length === 0 ? "overview" : (next?.step ?? "review"));
    }
  }, []);

  useEffect(() => {
    void load();
    return () => {
      loadSequence.current += 1;
    };
  }, [load]);

  const save = useCallback(async (step: string, payload: Record<string, unknown>) => {
    const response = await putWizardStep(step, payload, props.csrfToken);
    if (response) setData(response);
    return response;
  }, [props.csrfToken]);

  // N03: submit refusals have names (`salespage_approval_required`,
  // `state_conflict` on an already-submitted wizard). null = it went through.
  const submitWizard = useCallback(async (): Promise<ActionIssue | null> => {
    setSubmitting(true);
    try {
      const response = await fetch("/api/app/wizard/submit", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken,
          "Idempotency-Key": crypto.randomUUID()
        },
        body: JSON.stringify({})
      });
      if (!response.ok) return await actionIssueFrom(response);
      const body = await response.json().catch(() => null) as { ok?: boolean; error?: string } | null;
      if (body?.ok !== true) return actionIssueFromResponse(200, body?.error);
      const fresh = await fetchWizard();
      if (fresh.ok) setData(fresh.data);
      await props.onSubmitted();
      return null;
    } catch {
      return actionIssueFromThrown();
    } finally {
      setSubmitting(false);
    }
  }, [props.csrfToken, props.onSubmitted]);

  if (failure) {
    return <AppWizardLoadError failure={failure} partner={props.partner ?? null} t={props.t} onRetry={() => void load()} />;
  }
  if (!data) {
    return <article className="appwiz-card appwiz-loading"><div className="loading-pulse" /><p>{props.t("common.loading")}</p></article>;
  }

  const states = data.states;
  const activeState = states.some((state) => state.step === active) ? active : "overview";
  const ActiveComponent = STEP_COMPONENTS[activeState];
  const readOnly = data.readOnly;

  return (
    <article className="appwiz-card" aria-label={props.t("appWizard.title")}>
      <header className="appwiz-progress">
        <p>{props.t("appWizard.progressLabel")
          .replace("{done}", String(data.progress.done))
          .replace("{total}", String(data.progress.total))}
          <strong> {data.progress.percent}%</strong></p>
        <span className="appwiz-progress-bar" role="progressbar" aria-valuenow={data.progress.percent} aria-valuemin={0} aria-valuemax={100}>
          <span style={{ width: `${data.progress.percent}%` }} />
        </span>
      </header>
      <div className="appwiz-layout">
        <nav className="appwiz-nav" aria-label={props.t("appWizard.navLabel")}>
          <ol>
            {states.map((state, index) => (
              <li key={state.step}>
                <button
                  type="button"
                  className={`appwiz-nav-item ${state.status}${state.step === activeState ? " active" : ""}`}
                  aria-current={state.step === activeState ? "step" : undefined}
                  onClick={() => setActive(state.step)}
                >
                  <span className="appwiz-nav-mark" aria-hidden="true">
                    {state.status === "done" ? "✓" : index + 1}
                  </span>
                  <span>{props.t(`appWizard.steps.${state.step}`)}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
        <div className="appwiz-body">
          <ActiveComponent
            key={activeState}
            data={data}
            locale={props.locale}
            csrfToken={props.csrfToken}
            readOnly={readOnly}
            t={props.t}
            save={save}
            onOpenStep={(step) => setActive(step as AppWizardStep)}
            appleConfirmed={props.appleConfirmed}
            appleChecklist={activeState === "apple_developer" ? props.appleChecklist : undefined}
            onSubmitWizard={activeState === "review" ? submitWizard : undefined}
            submitting={submitting}
          />
        </div>
      </div>
    </article>
  );
}

export function AppWizardLoadError(props: {
  failure: WizardLoadFailure;
  partner: Pick<PartnerDto, "displayName" | "calendlyUrl"> | null;
  t: WizardT;
  onRetry: () => void;
}) {
  const reasonKey = props.failure.code === "salespage_approval_required"
    ? "appWizard.loadError.salespageApprovalRequired"
    : "appWizard.loadError.generic";
  const partnerName = props.partner?.displayName ?? props.t("appWizard.loadError.partnerFallback");
  return (
    <article className="appwiz-card appwiz-load-error">
      <div className="message error-message" role="alert">
        <p>{props.t(reasonKey)}</p>
        <button type="button" className="secondary-button" onClick={props.onRetry}>
          {props.t("appWizard.loadError.retry")}
        </button>
      </div>
      <p>
        {props.partner?.calendlyUrl ? (
          <a href={props.partner.calendlyUrl} target="_blank" rel="noreferrer">
            {props.t("appWizard.loadError.partnerCall")}
          </a>
        ) : props.t("appWizard.loadError.partnerFallback").replace("{partner}", partnerName)}
      </p>
    </article>
  );
}
